import { useState, useRef, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CatalogProductImage from './CatalogProductImage';

/**
 * Galería con swipe táctil y flechas (catálogo público).
 * @param {string[]} urls
 */
export default function CatalogImageGallery({
  urls = [],
  alt = '',
  fixedHeight,
  showImages = true,
  bgcolor = 'grey.100',
}) {
  const list = (urls || []).filter(Boolean);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    setIndex(0);
  }, [list.join('|')]);

  const count = list.length;
  const safeIndex = count > 0 ? Math.min(index, count - 1) : 0;
  const src = showImages && count > 0 ? list[safeIndex] : null;

  const go = useCallback(
    (delta) => {
      if (count <= 1) return;
      setIndex((i) => (i + delta + count) % count);
    },
    [count]
  );

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    const end = e.changedTouches?.[0]?.clientX;
    touchStartX.current = null;
    if (start == null || end == null || count <= 1) return;
    const diff = end - start;
    if (Math.abs(diff) < 40) return;
    if (diff < 0) go(1);
    else go(-1);
  };

  return (
    <Box sx={{ position: 'relative', touchAction: 'pan-y' }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <CatalogProductImage src={src} alt={alt} fixedHeight={fixedHeight} bgcolor={bgcolor} />

      {count > 1 && showImages && (
        <>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.92)',
              boxShadow: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
            }}
            aria-label="Foto anterior"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.92)',
              boxShadow: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
            }}
            aria-label="Foto siguiente"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              gap: 0.75,
              pointerEvents: 'none',
            }}
          >
            {list.map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: i === safeIndex ? 8 : 6,
                  height: i === safeIndex ? 8 : 6,
                  borderRadius: '50%',
                  bgcolor: i === safeIndex ? 'primary.main' : 'rgba(255,255,255,0.85)',
                  boxShadow: 1,
                  transition: 'width .15s, height .15s',
                }}
              />
            ))}
          </Box>

          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.45)',
              color: '#fff',
              fontWeight: 600,
            }}
          >
            {safeIndex + 1}/{count}
          </Typography>
        </>
      )}
    </Box>
  );
}
